import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Image,
  Search,
  Package,
  Check,
  X,
  Loader2,
  Clipboard,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Camera,
} from "lucide-react";
import { toast } from "sonner";
import JsBarcode from "jsbarcode";

const API = process.env.REACT_APP_BACKEND_URL;

// Barcode Component using JsBarcode
const ProductBarcode = ({ barcode, className = "" }) => {
  const svgRef = useRef(null);
  
  useEffect(() => {
    if (svgRef.current && barcode) {
      try {
        JsBarcode(svgRef.current, barcode, {
          format: "CODE128",
          width: 1.5,
          height: 35,
          displayValue: true,
          fontSize: 10,
          margin: 2,
          background: "transparent"
        });
      } catch (e) {
        console.error("Barcode generation error:", e);
      }
    }
  }, [barcode]);
  
  if (!barcode) return null;
  
  return <svg ref={svgRef} className={className} />;
};

const ProductImageUpload = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [category, setCategory] = useState("all");
  const [supplier, setSupplier] = useState("all");
  const [search, setSearch] = useState("");
  const [showWithImages, setShowWithImages] = useState("all"); // all, with, without
  
  // Pagination
  const [page, setPage] = useState(0);
  const limit = 24;
  
  // Selected product for image upload
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pastedImage, setPastedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const pasteAreaRef = useRef(null);
  const [syncing, setSyncing] = useState(false);

  const syncFromR2 = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/api/product-images/sync-from-r2`);
      const data = response.data;
      if (data.success) {
        toast.success(`Synced ${data.products_updated} products with R2 images`);
        if (data.already_correct > 0) {
          toast.info(`${data.already_correct} products already had correct image URLs`);
        }
        if (data.total_not_found > 0) {
          toast.warning(`${data.total_not_found} R2 files didn't match any PLU codes`);
        }
        // Refresh stats and products
        fetchStats();
        fetchProducts();
      }
    } catch (error) {
      console.error("Error syncing from R2:", error);
      toast.error("Failed to sync images from R2");
    } finally {
      setSyncing(false);
    }
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString(),
      });
      
      if (category && category !== "all") params.append("category", category);
      if (supplier && supplier !== "all") params.append("supplier", supplier);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/api/website/products?${params.toString()}`);
      let prods = response.data.products || [];
      
      // Filter by image status
      if (showWithImages === "with") {
        prods = prods.filter(p => p.image_url);
      } else if (showWithImages === "without") {
        prods = prods.filter(p => !p.image_url);
      }
      
      setProducts(prods);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [page, category, supplier, search, showWithImages]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/website/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/suppliers`);
      // Sort suppliers alphabetically by name
      const sortedSuppliers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setSuppliers(sortedSuppliers);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/product-images/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
    fetchStats();
  }, [fetchCategories, fetchSuppliers, fetchStats]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Handle paste event
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        const reader = new FileReader();
        reader.onload = (event) => {
          setPastedImage(event.target.result);
        };
        reader.readAsDataURL(blob);
        e.preventDefault();
        break;
      }
    }
  }, []);

  // Add paste listener when dialog opens
  useEffect(() => {
    if (uploadDialogOpen) {
      document.addEventListener("paste", handlePaste);
      return () => document.removeEventListener("paste", handlePaste);
    }
  }, [uploadDialogOpen, handlePaste]);

  const openUploadDialog = (product) => {
    setSelectedProduct(product);
    setPastedImage(null);
    setUploadDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!pastedImage || !selectedProduct) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image_data", pastedImage);
      
      await axios.post(
        `${API}/api/product-images/upload/${selectedProduct.id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      toast.success(`Image saved for ${selectedProduct.name}`);
      setUploadDialogOpen(false);
      setPastedImage(null);
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error("Upload error:", error);
      const detail = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to upload image — ${detail}`, { duration: 10000 });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (product) => {
    if (!confirm(`Remove image from ${product.name}?`)) return;
    
    try {
      await axios.delete(`${API}/api/product-images/${product.id}`);
      toast.success("Image removed");
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to remove image");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(price || 0);
  };

  const totalPages = Math.ceil(total / limit);
  
  // Find current category stats
  const currentCategoryStats = stats?.by_category?.find(
    c => c._id?.toUpperCase() === category?.toUpperCase()
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Camera className="h-6 w-6 text-blue-600" />
            Product Image Upload
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Click a product, then paste (Ctrl+V) an image from your clipboard
          </p>
        </div>
        
        {/* Overall Stats and Sync Button */}
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={syncFromR2}
            disabled={syncing}
            className="flex items-center gap-2"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Image className="h-4 w-4" />
            )}
            {syncing ? "Syncing..." : "Sync from R2"}
          </Button>
          
          {stats && (
            <div className="text-right text-sm">
              <p className="text-gray-500">Overall Coverage</p>
              <p className="text-2xl font-bold text-blue-600">{stats.coverage_percent}%</p>
              <p className="text-xs text-gray-400">
                {stats.with_images.toLocaleString()} / {stats.total_products.toLocaleString()} products
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Category Stats */}
      {currentCategoryStats && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-blue-800">{category} Progress</p>
                <p className="text-sm text-blue-600">
                  {currentCategoryStats.with_image} of {currentCategoryStats.total} products have images
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-blue-700">
                  {Math.round((currentCategoryStats.with_image / currentCategoryStats.total) * 100)}%
                </p>
              </div>
            </div>
            <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 rounded-full transition-all"
                style={{ width: `${(currentCategoryStats.with_image / currentCategoryStats.total) * 100}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="w-48">
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(0); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                <SelectItem key={cat.name} value={cat.name}>
                  {cat.name} ({cat.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="w-56">
          <Select value={supplier} onValueChange={(v) => { setSupplier(v); setPage(0); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select supplier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Suppliers</SelectItem>
              {[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((sup) => (
                <SelectItem key={sup.id} value={sup.name}>
                  {sup.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="pl-9"
          />
        </div>
        
        <Select value={showWithImages} onValueChange={(v) => { setShowWithImages(v); setPage(0); }}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            <SelectItem value="without">Without Images</SelectItem>
            <SelectItem value="with">With Images</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg">No products found</p>
          <p className="text-sm">Try changing the category or search term</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {products.map((product) => (
            <Card 
              key={product.id} 
              className={`cursor-pointer hover:shadow-lg transition-all overflow-hidden group ${
                product.image_url ? "ring-2 ring-green-400" : ""
              }`}
              onClick={() => openUploadDialog(product)}
            >
              <CardContent className="p-3">
                {/* Image Area */}
                <div className="aspect-square bg-white rounded-lg mb-2 flex items-center justify-center overflow-hidden relative border">
                  {product.image_url ? (
                    <>
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="max-w-full max-h-full object-contain p-1"
                      />
                      <div className="absolute top-1 right-1">
                        <Badge className="bg-green-500 text-white text-xs">
                          <Check className="h-3 w-3" />
                        </Badge>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); handleDeleteImage(product); }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <div className="text-center text-gray-400">
                      <Image className="h-8 w-8 mx-auto mb-1" />
                      <p className="text-xs">Click to add</p>
                    </div>
                  )}
                </div>
                
                {/* Product Info */}
                <p className="text-xs text-gray-500 truncate">{product.plu_code}</p>
                <p className="text-sm font-medium line-clamp-2 min-h-[2.5rem]">{product.name}</p>
                <p className="text-sm font-bold text-blue-600">{formatPrice(product.selling_price_incl_vat)}</p>
                
                {/* Barcode */}
                {product.barcode && (
                  <div className="mt-1 flex justify-center">
                    <ProductBarcode barcode={product.barcode} className="max-w-full" />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4">
          <Button
            variant="outline"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Add Image
            </DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              {/* Product Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">{selectedProduct.plu_code}</p>
                <p className="font-medium">{selectedProduct.name}</p>
                <p className="text-sm text-blue-600">{formatPrice(selectedProduct.selling_price_incl_vat)}</p>
                
                {/* Barcode in dialog */}
                {selectedProduct.barcode && (
                  <div className="mt-2 flex justify-center border-t pt-2">
                    <ProductBarcode barcode={selectedProduct.barcode} />
                  </div>
                )}
              </div>
              
              {/* Paste Area */}
              <div 
                ref={pasteAreaRef}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  pastedImage ? "border-green-400 bg-green-50" : "border-gray-300 hover:border-blue-400"
                }`}
              >
                {pastedImage ? (
                  <div className="space-y-3">
                    <img 
                      src={pastedImage} 
                      alt="Preview" 
                      className="max-h-48 mx-auto rounded-lg shadow"
                    />
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPastedImage(null)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <Clipboard className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                    <p className="font-medium">Paste image here</p>
                    <p className="text-sm">Copy an image, then press Ctrl+V</p>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpload}
                  disabled={!pastedImage || uploading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Save Image
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductImageUpload;
